<template>
  <StatusPopover
    title="Appointment Status"
    :status="status"
    :status-options="filterStatusOptions"
    @save-status="saveStatus"
    @update:status="$emit('update:status', $event)"
  >
    <template #default="{ changedStatus }">
      <q-item-wrapper
        v-if="canEdit && (status === 'SCHEDULED' || status === 'UNCONFIRMED')"
        clickable
        icon="mdi-calendar-remove"
        label="Mark as unscheduled"
        caption="The appointment will be placed in the unscheduled sidebar"
        icon-style="margin-left: -4px"
        @click="markAsUnscheduled"
      />

      <q-item-wrapper v-if="showNote && can(permissions.canAccessCalendar)" dense>
        <q-btn
          v-close-popup
          size="12px"
          label="Add Note"
          icon-right="mdi-clipboard-file-outline"
          color="blue"
          class="full-width"
          @click="$emit('focusOnNote')"
        />
      </q-item-wrapper>

      <q-item-wrapper v-if="showConfirmationEmailOption && hasClients" dense>
        <q-btn
          v-close-popup
          size="12px"
          label="Send Confirmation"
          icon-right="mdi-send-check"
          color="green"
          class="full-width"
          @click="$emit('sendConfirmationEmail')"
        />
      </q-item-wrapper>

      <q-item-wrapper v-if="changedStatus && (canSendMessage || can(permissions.canAccessCalendar))" dense>
        <q-btn
          v-close-popup
          size="12px"
          label="Notify Client"
          icon-right="mdi-message-plus"
          color="blue"
          @click="$emit('openManualReminder')"
        />
      </q-item-wrapper>

      <div class="q-pb-sm" />
    </template>
  </StatusPopover>
</template>

<script>
import StatusPopover from 'components/partials/StatusPopover'
import contextMenuMixin from '../mixins/contextMenuMixin'
import statusMixin from '../mixins/statusMixin'

/**
 * This component acts as the main popup for setting the status of an appointment
 */
export default {
  components: {
    StatusPopover
  },
  mixins: [statusMixin, contextMenuMixin],
  props: {
    id: {
      type: Number,
      required: true
    },
    status: {
      type: String,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    canSendMessage: {
      type: Boolean,
      default: false
    },
    hasClients: {
      type: Boolean,
      default: false
    },
    // Show option of sending a message after they have changed the status
    withSendMessage: {
      type: Boolean,
      default: false
    },
    // Whether all required forms have been completed
    hasAllRequiredForms: {
      type: Boolean,
      default: true
    },
    // Whether all required checklists have been completed
    hasAllRequiredChecklists: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:status', 'focusOnNote', 'openManualReminder', 'sendConfirmationEmail'],
  data () {
    return {
      showNote: false
    }
  },
  computed: {
    company () {
      return this.$store.state.company.company
    },
    filterStatusOptions () {
      const options = this.baseStatusOptions(false, false)
      // Disable COMPLETE option if required forms are not all completed and locking is enabled
      if (!this.hasAllRequiredForms && this.company.appointments_lock_complete_without_forms) {
        const completeOption = options.find(o => o.value === 'COMPLETE')
        if (completeOption) {
          completeOption.disable = true
          completeOption.caption = 'Required forms must be completed first'
        }
      }
      // Disable COMPLETE option if required checklists are not all completed and locking is enabled
      if (!this.hasAllRequiredChecklists && this.company.appointments_lock_complete_without_checklists) {
        const completeOption = options.find(o => o.value === 'COMPLETE')
        if (completeOption) {
          completeOption.disable = true
          completeOption.caption = completeOption.caption
            ? completeOption.caption + '. Required checklist items must also be completed'
            : 'Required checklist items must be completed first'
        }
      }
      return options
    },
    showConfirmationEmailOption () {
      // Show option to send confirmation email only for SCHEDULED appointments
      return this.status === 'SCHEDULED'
    }
  },
  methods: {
    saveStatus (callback) {
      this.baseSetStatus(this.id, this.status, () => {
        if (this.status === 'DEPARTED_INCOMPLETE' || this.status === 'CANCELLED') {
          this.showNote = true
        }
        callback(this.withSendMessage && this.canSendMessage && this.hasClients)
      })
    },
    markAsUnscheduled () {
      this.baseMarkAsUnscheduled(this.id)
      this.$emit('update:status', 'UNSCHEDULED')
    }
  }
}
</script>
